"use client";

import { useEffect, useRef, useState } from "react";
import { Loader2, Send, CheckCircle, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/use-i18n";
import { toast } from "sonner";
export function SuggestionForm() {
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 2026-09-23 FASE 4 transversal M1 (research 2026 W3C WCAG 2.2 3.3.1 Error
  // Identification + MDN aria-invalid 2026: el email inválido por regex local
  // es error POR CAMPO — antes solo vivía en el role=alert global sin marcar
  // el campo, el SR no asociaba qué corregir. Ahora aria-invalid + descrito
  // por el mismo alert; el error global de red/servidor sigue sin marcar
  // campos (paridad ContactForm). Sin cambio visual, sin keys nuevas — reusa
  // newsletterInvalidEmail existente en 14).
  const [emailInvalid, setEmailInvalid] = useState(false);
  const errorRef = useRef<HTMLParagraphElement>(null);

  // P2 a11y — paridad ContactForm: al aparecer el error, mover foco a él
  // para lectores de pantalla (2026-09-23 FASE 4 transversal M1; research
  // 2026 W3C WCAG 2.2 3.3.1 Error Identification pub. 2026-06-12 +
  // accessibility.build 2026: submit fallido debe mover foco al resumen/
  // primer error con tabindex="-1"; NewsletterSignup ya mueve foco al
  // campo, ContactForm al role=alert — SuggestionForm era el único sin
  // foco. Sin cambio visual: solo foco programático + outline-none con
  // focus-visible del sistema).
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setEmailInvalid(false);

    // Honeypot: bots fill it — fake success without hitting the API.
    if (honeypot) {
      toast.success(t("suggestionsSuccess"), {
        icon: <CheckCircle className="size-4 text-[var(--success)]" />,
        duration: 6000
      });
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setHoneypot("");
      return;
    }

    // Normalize like Zod (.trim().toLowerCase().email()) + server schema.
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setEmailInvalid(true);
      setError(t("newsletterInvalidEmail"));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("/api/suggestions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name,
          email: normalizedEmail,
          subject,
          message,
          honeypot
        })
      });
      const data = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      if (!response.ok) {
        setError(data.error ?? t("suggestionsErrorSend"));
        return;
      }
      toast.success(t("suggestionsSuccess"), {
        icon: <CheckCircle className="size-4 text-[var(--success)]" />,
        duration: 6000
      });
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setHoneypot("");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // 2026-09-23 FASE 4 transversal M4 (research 2026 OWASP Logging Cheat
        // Sheet + GDPR art.5(1)(c) minimización: el objeto err crudo puede
        // arrastrar email/message del formulario vía fetch context — solo
        // mensaje truncado 120 chars, nunca PII completa).
        const safeMsg = err instanceof Error ? err.message.slice(0, 120) : "unknown";
        console.error("[SuggestionForm] Submit failed:", safeMsg);
      }
      setError(t("suggestionsErrorConnection"));
      toast.error(t("suggestionsErrorConnection"));
    } finally {
      setLoading(false);
    }
  };
  return <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot anti-spam: hidden from users, bots fill it. Validated server-side (max 0). */}
      <input type="text" name="honeypot" value={honeypot} onChange={event_0 => setHoneypot(event_0.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="suggestion-name" className="text-sm font-medium sm:text-[15px]">
            {t("contactNameLabel")}
          </label>
          <Input id="suggestion-name" name="name" type="text" autoComplete="name" required minLength={2} maxLength={80} value={name} onChange={event_1 => setName(event_1.target.value)} className="h-12 bg-card px-4 text-[15px] sm:text-[16px]" />
        </div>
        <div className="space-y-2">
          <label htmlFor="suggestion-email" className="text-sm font-medium sm:text-[15px]">
            {t("contactEmailLabel")}
          </label>
          <Input id="suggestion-email" name="email" type="email" autoComplete="email" required maxLength={160} value={email} onChange={event_2 => {
          setEmail(event_2.target.value);
          if (emailInvalid) setEmailInvalid(false);
        }} aria-invalid={emailInvalid ? true : undefined} aria-describedby={emailInvalid || error ? "suggestion-error" : undefined} className="h-12 bg-card px-4 text-[15px] sm:text-[16px]" />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="suggestion-subject" className="text-sm font-medium sm:text-[15px]">
          {t("suggestionsSubjectLabel")}
        </label>
        <Input id="suggestion-subject" name="subject" type="text" required minLength={3} maxLength={120} value={subject} onChange={event_3 => setSubject(event_3.target.value)} placeholder={t("suggestionsSubjectPlaceholder")} className="h-12 bg-card px-4 text-[15px] sm:text-[16px]" />
      </div>

      <div className="space-y-2">
        <label htmlFor="suggestion-message" className="text-sm font-medium sm:text-[15px]">
          {t("suggestionsMessageLabel")}
        </label>
        <Textarea id="suggestion-message" name="message" required minLength={10} maxLength={2000} rows={6} value={message} onChange={event_4 => setMessage(event_4.target.value)} placeholder={t("suggestionsMessagePlaceholder")} className="min-h-[140px] w-full resize-y text-[15px] sm:text-[16px]" />
      </div>

      {error ?
    // 2026-09-21 FASE 3 FIXER a11y (research 2026 WCAG 2.2 3.3.1: error global
    // solo en role=alert separado, sin aria-invalid por campo — paridad ContactForm).
    <p id="suggestion-error" ref={errorRef} tabIndex={-1} role="alert" className="text-sm text-[var(--danger)] outline-none sm:text-[15px]">
          {error}
        </p> : null}

      <Button type="submit" variant="accent" className="btn-shimmer h-12 w-full text-[15px] font-medium sm:text-[16px]" disabled={loading} aria-busy={loading}>
        {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Send className="size-4" aria-hidden="true" />}
        {t("suggestionsSubmit")}
      </Button>

      <p className="flex items-start gap-2 text-[13px] leading-relaxed text-muted-foreground sm:text-[14px]">
        <Lightbulb className="mt-0.5 size-4 shrink-0 text-[var(--accent)]" aria-hidden="true" />
        <span>{t("suggestionsFormDisclaimer")}</span>
      </p>
    </form>;
}